import React, { useMemo } from 'react';
import formatMoney from '@/lib/formatMoney';
import Badge from '../UI/Badge';

export default function BacktestOrderBook({ trades, snapshots, playbackIndex }) {
  // Show the orders/fills state at the current playback position
  const { visibleTrades, pendingOrders } = useMemo(() => {
    if (!trades) return { visibleTrades: [], pendingOrders: [] };

    const maxIdx = playbackIndex ?? Infinity;
    const visible = trades.filter((t) => t.barIndex <= maxIdx);

    // Get pending orders from the nearest snapshot
    let pending = [];
    if (snapshots?.length > 0 && playbackIndex != null) {
      const snap = snapshots.reduce((best, s) =>
        s.barIndex <= playbackIndex && s.barIndex > (best?.barIndex ?? -1) ? s : best
      , null);
      pending = snap?.pendingOrders || [];
    }

    return { visibleTrades: visible.slice(-50).reverse(), pendingOrders: pending };
  }, [trades, snapshots, playbackIndex]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Pending orders section */}
      {pendingOrders.length > 0 && (
        <div className="mb-3">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 mb-1.5 px-1">
            Pending Orders ({pendingOrders.length})
          </p>
          <div className="space-y-1 max-h-[150px] overflow-y-auto">
            {pendingOrders.slice(0, 20).map((o) => (
              <div key={o.id} className="flex items-center justify-between text-xs px-2 py-1.5 rounded bg-accent/10">
                <div className="flex items-center gap-1.5">
                  <Badge variant={o.side === 'buy' ? 'success' : 'danger'} className="text-[10px] px-1 py-0">
                    {o.side.toUpperCase()}
                  </Badge>
                  <span className="text-muted-foreground">
                    {parseFloat(o.quantity).toFixed(2)}
                  </span>
                </div>
                <span className="font-mono text-foreground">
                  {formatMoney(o.limit_price)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filled orders (trade history) */}
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground/60 mb-1.5 px-1">
        Filled Orders ({visibleTrades.length})
      </p>
      <div className="flex-1 overflow-y-auto space-y-0.5">
        {visibleTrades.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">No fills yet</div>
        ) : (
          visibleTrades.map((t, i) => (
            <div
              key={`${t.barIndex}-${t.side}-${i}`}
              className="flex items-center justify-between text-xs px-2 py-1.5 rounded hover:bg-accent/10 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Badge variant={t.side === 'buy' ? 'success' : 'danger'} className="text-[10px] px-1 py-0">
                  {t.side.toUpperCase()}
                </Badge>
                <span className="text-muted-foreground">
                  {parseFloat(t.quantity).toFixed(4)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-foreground">{formatMoney(t.price)}</span>
                {t.realizedPnL !== 0 && (
                  <span className={`font-mono ${t.realizedPnL > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {formatMoney(t.realizedPnL, { sign: true })}
                  </span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
